<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import StatusTag from '@/components/table/StatusTag.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import {getSupplierList} from '@/api/suppliers'

const router = useRouter()
const supplierList = ref<any>([])
const loading = ref<boolean>(true)
const searchQuery = ref('')
const statusFilters = ref<string[]>([])

const filteredSupplierList = computed(() => {
  const query = searchQuery.value.toLowerCase()
  let filtered = supplierList.value.filter(
      (supplier: { name: string; email: string; contactPerson: string }) =>
          supplier.name.toLowerCase().includes(query) ||
          supplier.contactPerson.toLowerCase().includes(query)
  )

  if (statusFilters.value.length > 0 && !statusFilters.value.includes('all')) {
    filtered = filtered.filter((supplier: { status?: string }) => {
      const supplierStatus = supplier.status || 'active'
      return statusFilters.value.includes(supplierStatus)
    })
  }

  return filtered
})

const handleFilterChange = (filters: string[]) => {
  statusFilters.value = filters
}

onMounted(async () => {
  supplierList.value = await getSupplierList()
  loading.value = false
})
</script>

<template>
  <PageHeader
      title="Suppliers"
      :description="`Total: ${supplierList.length} | Filtered: ${filteredSupplierList.length}`"
      buttonText="Add Supplier"
      buttonIcon="mdi:truck-fast"
      :buttonClick="() => router.push('/suppliers/add')"
  />

  <DataTable
      :value="filteredSupplierList"
      paginator
      :rows="10"
      tableStyle="min-width: 50rem"
      stripedRows
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      :rowsPerPageOptions="[10, 25]"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} suppliers"
      :loading="loading"
      :pt="{
      table: loading && 'min-h-[30rem]'
    }"
      :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No suppliers found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

        <TableFilter
            :filter-options="[
              { value: 'all', label: 'All', id: 'supplier-status-all' },
              { value: 'active', label: 'Active', id: 'supplier-status-active' },
              { value: 'inactive', label: 'Inactive', id: 'supplier-status-inactive' }
            ]"
            filter-title="Filter Supplier Status"
            @filter-change="handleFilterChange"
        />

      </div>
    </template>

    <Column field="name" header="Supplier Name" sortable>
      <template #body="slotProps">
        <RouterLink
            :to="`/suppliers/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.name }}
        </RouterLink>
      </template>
    </Column>
    <Column field="contactPerson" header="Contact Person" sortable />
    <Column field="status" header="Status" sortable>
      <template #body="slotProps">
        <StatusTag :status="slotProps.data.status ?? (slotProps.data.isActive ? 'active' : 'inactive')"/>
      </template>
    </Column>
    <Column field="action">
      <template #body="slotProps">
        <RouterLink
            :to="`/suppliers/${slotProps.data.id}`"
            class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
              icon="material-symbols:more-up"
              class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>

<style></style>
